import { useEffect, useMemo, useRef, useState } from 'react';

import { useQueryClient } from '@tanstack/react-query';

import { propertyQueryKeys } from '~/hooks/apis/accommodation/useGetProperty';

import ReservationService from '~/services/ReservationService';

import type { LocalImageEdit } from './ImagesVideo/ImageEditor/ImageEditorUtils';
import type { RoomImageOrderOverrides } from './ImagesVideo/ImagesByRoom/utils';
import { getRoomTypeImageWriteVersion } from './ImagesVideo/mediaCache';
import type { MediaImage, MediaSourceImage, PropertyRoomTypeWithImages } from './ImagesVideo/types';
import {
  buildMediaImages,
  dedupeMediaImages,
  getMediaImageSourceKey,
  getPrimaryVideoId,
  isRemovedRoomGalleryImage,
} from './ImagesVideo/utils';
import type { RemovedRoomImageIdsByRoomKey, UploadedRoomAssignment } from './ImagesVideo/utils';
import type { OfferBuilderProperty } from './PropertyInformation/types';
import useOfferBuilderMediaEdits from './useOfferBuilderMediaEdits';
import useOfferBuilderMediaImport, { type SyncAllImagesResult } from './useOfferBuilderMediaImport';
import useOfferBuilderMediaOrder from './useOfferBuilderMediaOrder';
import useOfferBuilderMediaUpload, { type UploadFilesOptions } from './useOfferBuilderMediaUpload';
import useOfferBuilderMediaVisibility from './useOfferBuilderMediaVisibility';

export type { SyncFailureReason, SyncAllImagesResult } from './useOfferBuilderMediaImport';

interface UseOfferBuilderMediaParams {
  offerId: string;
  images?: Array<App.OfferImage>;
  propertyName?: string;
  propertyRoomTypes?: Array<PropertyRoomTypeWithImages>;
  videos?: {
    luxuryescapes?: {
      vimeo_id?: string | null;
    };
  };
}

export interface OfferBuilderMediaController {
  removedRoomImageIdsByRoomKey: RemovedRoomImageIdsByRoomKey;
  hasUnsavedOfferChanges: boolean;
  hasUnsavedRoomImageChanges: boolean;
  hasUnsavedRoomImageTitleEdits: boolean;
  hiddenImagePublicIds: Array<string>;
  imageEdits: Record<string, LocalImageEdit>;
  imagePublicIdReplacements: Record<string, string>;
  offerImageOrderOverrides: Array<string>;
  savedOfferGalleryOrder: Array<string>;
  savedRoomImageOrders: RoomImageOrderOverrides;
  isUploadingImages: boolean;
  isSavingChanges: boolean;
  pendingOversizedUpload?: { file: File; filename: string };
  mediaImages: Array<MediaImage>;
  roomImagePendingDeletion?: MediaImage;
  roomImageOrderOverrides: RoomImageOrderOverrides;
  roomImageOrderResetKey: number;
  roomImagePendingEdit?: MediaImage;
  uploadedRoomAssignments: Array<UploadedRoomAssignment>;
  videoId?: string;
  closeRoomImageDeleteDialog: () => void;
  confirmRemoveRoomImage: () => Promise<void>;
  deleteImage: (publicId: string, options?: { showError?: boolean; showSuccess?: boolean }) => Promise<void>;
  discardRoomImageTitleEdits: () => void;
  removeRoomImageFromEditor: (image: MediaImage) => void;
  removeImageFromRoom: (image: MediaImage, options?: { showError?: boolean; showSuccess?: boolean }) => Promise<void>;
  reorderOfferGalleryImages: (orderedPublicIds: Array<string>) => void;
  reorderRoomImages: (roomKey: string, orderedPublicIds: Array<string>) => void;
  saveRoomImageChanges: (roomKey?: string, options?: { showError?: boolean; showSuccess?: boolean }) => Promise<void>;
  saveImagesVideoChanges: () => Promise<void>;
  discardChanges: (scope?: 'all' | 'offer' | 'room') => void;
  saveRoomImageTitleEdits: (roomKey: string) => Promise<void>;
  saveImageEdit: (publicId: string, edit: LocalImageEdit, persistedPublicId?: string) => Promise<void>;
  setRoomImagePendingEdit: (image?: MediaImage) => void;
  setVideoId: (videoId?: string) => void;
  toggleHiddenImage: (publicId: string) => Promise<void>;
  updateImageTitle: (roomKey: string, publicId: string, title: string) => void;
  uploadFiles: (files: Array<File>, options?: UploadFilesOptions) => Promise<'uploaded' | 'queued' | 'failed'>;
  uploadFilesToOfferGallery: (files: Array<File>) => Promise<void>;
  uploadFilesToRoom: (roomId: string, roomName: string, files: Array<File>) => Promise<void>;
  cancelOversizedUpload: () => void;
  confirmOversizedUpload: (file: File) => Promise<boolean>;
  editRoomImage: (publicId: string) => void;
  importImages: (photoUnits: Array<{ id_cloudinary_external: string; title?: string }>) => Promise<number>;
  importRoomImages: (
    roomKey: string,
    photoUnits: Array<{ id_cloudinary_external: string; title?: string }>,
  ) => Promise<number>;
  syncGalleryImages: (propertyId: string | undefined) => Promise<void>;
  syncTopRoomImages: (roomKey: string) => Promise<void>;
  syncAllImages: (propertyId: string | undefined) => Promise<SyncAllImagesResult>;
}

export default function useOfferBuilderMedia({
  offerId,
  images = [],
  propertyName,
  propertyRoomTypes,
  videos,
}: UseOfferBuilderMediaParams): OfferBuilderMediaController {
  const queryClient = useQueryClient();
  const hydratedRoomTypesKeyRef = useRef('');
  const [deletedImageSourceKeys, setDeletedImageSourceKeys] = useState<Array<string>>([]);
  const [removedRoomImageIdsByRoomKey, setRemovedRoomImageIdsByRoomKey] = useState<RemovedRoomImageIdsByRoomKey>({});
  const [replacedImageSourceKeys, setReplacedImageSourceKeys] = useState<Array<string>>([]);
  const [localVideoId, setLocalVideoId] = useState<string | undefined>();
  const videoId = localVideoId === '' ? undefined : (localVideoId ?? getPrimaryVideoId(videos));

  const [hiddenImageOverrides, setHiddenImageOverrides] = useState<Record<string, boolean>>({});

  const mediaImagesBase = useMemo(
    () => buildMediaImages({ offerImages: images, propertyRoomTypes }),
    [images, propertyRoomTypes],
  );

  const upload = useOfferBuilderMediaUpload({
    offerId,
    propertyRoomTypes,
  });

  const mediaImages = useMemo(() => {
    const deletedImageSourceKeySet = new Set(deletedImageSourceKeys);
    const replacedImageSourceKeySet = new Set(replacedImageSourceKeys);

    return dedupeMediaImages(mediaImagesBase.concat(upload.uploadedImages))
      .filter((image) => !deletedImageSourceKeySet.has(getMediaImageSourceKey(image)))
      .filter((image) => !replacedImageSourceKeySet.has(getMediaImageSourceKey(image)))
      .filter((image) => !isRemovedRoomGalleryImage(image, removedRoomImageIdsByRoomKey))
      .map((image) =>
        hiddenImageOverrides[image.publicId] === undefined
          ? image
          : { ...image, hidden: hiddenImageOverrides[image.publicId] },
      );
  }, [
    deletedImageSourceKeys,
    hiddenImageOverrides,
    mediaImagesBase,
    removedRoomImageIdsByRoomKey,
    replacedImageSourceKeys,
    upload.uploadedImages,
  ]);

  const mediaImport = useOfferBuilderMediaImport({
    offerId,
    offerImages: images,
    propertyName,
    propertyRoomTypes,
    mediaImages,
    setUploadedImages: upload.setUploadedImages,
    setDeletedImageSourceKeys,
    setRemovedRoomImageIdsByRoomKey,
  });

  // Room type images aren't in the offer's property payload, so hydrate the cache once.
  const propertyId = propertyRoomTypes?.find((room) => room.property_id)?.property_id;

  useEffect(() => {
    const roomsToHydrate = (propertyRoomTypes ?? []).filter((room) => room.id && room.property_id);

    if (!propertyId || roomsToHydrate.length === 0) {
      return;
    }

    const hydrationKey = `${propertyId}:${roomsToHydrate.map((room) => room.id).join(',')}`;

    if (hydratedRoomTypesKeyRef.current === hydrationKey) {
      return;
    }

    let cancelled = false;

    void (async () => {
      const hydratedRooms = await Promise.all(
        roomsToHydrate.map(async (room) => {
          const roomPropertyId = room.property_id;
          const roomTypeId = room.id;

          if (!roomPropertyId || !roomTypeId) {
            return { roomTypeId: '', images: [] as Array<MediaSourceImage> };
          }

          // Snapshot before the fetch so a concurrent import/sync write for this room can be
          // detected below and skipped instead of clobbered.
          const writeVersionBeforeFetch = getRoomTypeImageWriteVersion(roomPropertyId, roomTypeId);
          const response = await ReservationService.getRoomTypeImages(roomPropertyId, roomTypeId);

          return {
            roomTypeId,
            images: (response.result ?? []) as Array<MediaSourceImage>,
            isStale: getRoomTypeImageWriteVersion(roomPropertyId, roomTypeId) !== writeVersionBeforeFetch,
          };
        }),
      ).then((rooms) => rooms.filter((room) => room.roomTypeId && !room.isStale));

      if (cancelled) {
        return;
      }

      queryClient.setQueryData(
        propertyQueryKeys.detail(propertyId),
        (oldProperty: OfferBuilderProperty | undefined) => {
          if (!oldProperty?.room_types) {
            return oldProperty;
          }

          const imagesByRoomTypeId = new Map(hydratedRooms.map((room) => [room.roomTypeId, room.images]));

          return {
            ...oldProperty,
            room_types: oldProperty.room_types.map((roomType) => {
              const images = imagesByRoomTypeId.get(roomType.id);

              return images ? { ...roomType, images } : roomType;
            }),
          };
        },
      );

      hydratedRoomTypesKeyRef.current = hydrationKey;
    })();

    return () => {
      cancelled = true;
    };
  }, [propertyId, propertyRoomTypes, queryClient]);

  const edits = useOfferBuilderMediaEdits({
    offerId,
    mediaImages,
    propertyRoomTypes,
    setHiddenImageOverrides,
    replaceUploadedImage: (_sourceKey, nextImage) => {
      const nextImageSourceKey = getMediaImageSourceKey(nextImage);

      setReplacedImageSourceKeys((currentKeys) => {
        // The new image's own key must never stay blacklisted, otherwise reverting an edit
        // back to a previously-replaced publicId (e.g. the original) hides it for good.
        const nextKeys = currentKeys.filter((key) => key !== nextImageSourceKey);

        return nextKeys.includes(_sourceKey) ? nextKeys : nextKeys.concat(_sourceKey);
      });
      upload.replaceUploadedImage(nextImage.publicId, nextImage);
    },
  });

  const mediaImagesWithTitles = useMemo(
    () =>
      mediaImages.map((image) => {
        const imageSourceKey = getMediaImageSourceKey(image);
        const imageEdit = edits.imageEdits[imageSourceKey] ?? edits.imageEdits[image.publicId];

        return {
          ...image,
          title: imageEdit?.title ?? edits.savedImageTitles[imageSourceKey] ?? image.title,
        };
      }),
    [edits.imageEdits, edits.savedImageTitles, mediaImages],
  );

  const markRoomImageRemoved = (roomKey: string, publicId: string) => {
    setRemovedRoomImageIdsByRoomKey((currentRemovedImagesByRoomKey) => ({
      ...currentRemovedImagesByRoomKey,
      [roomKey]: [...new Set((currentRemovedImagesByRoomKey[roomKey] ?? []).concat(publicId))],
    }));
  };

  const markDeletedOfferImage = (publicId: string) => {
    const deletedSourceKeys = mediaImagesWithTitles
      .filter((mediaImage) => mediaImage.publicId === publicId && mediaImage.sourceType === 'offer')
      .map((mediaImage) => getMediaImageSourceKey(mediaImage));

    if (deletedSourceKeys.length === 0) {
      return;
    }

    setDeletedImageSourceKeys((currentKeys) => [...new Set(currentKeys.concat(deletedSourceKeys))]);
  };

  const order = useOfferBuilderMediaOrder({
    offerId,
    offerImages: images,
    uploadedImages: upload.uploadedImages,
    mediaImages: mediaImagesWithTitles,
    propertyRoomTypes,
    imagePublicIdReplacements: edits.imagePublicIdReplacements,
    removedRoomImageIdsByRoomKey,
  });

  const visibility = useOfferBuilderMediaVisibility({
    offerId,
    mediaImages: mediaImagesWithTitles,
    hiddenImageOverrides,
    setHiddenImageOverrides,
    removeUploadedImage: upload.removeUploadedImage,
    markDeletedOfferImage,
    markRoomImageRemoved,
    cleanupDeletedOfferGalleryImage: (publicId, deletedImageWasPending) =>
      order.syncOrdersAfterDelete('offer', undefined, publicId, deletedImageWasPending),
    cleanupDeletedRoomGalleryImage: (roomKey, publicId, deletedImageWasPending) =>
      order.syncOrdersAfterDelete('room', roomKey, publicId, deletedImageWasPending),
    clearImageEdit: edits.clearImageEdit,
    setRoomImagePendingEdit: edits.setRoomImagePendingEdit,
  });

  const hiddenImagePublicIds = mediaImagesWithTitles.filter((image) => image.hidden).map((image) => image.publicId);

  return {
    removedRoomImageIdsByRoomKey,
    hasUnsavedOfferChanges: order.hasUnsavedOfferChanges,
    hasUnsavedRoomImageChanges: order.hasUnsavedRoomImageChanges,
    hasUnsavedRoomImageTitleEdits: edits.hasUnsavedRoomImageTitleEdits,
    hiddenImagePublicIds,
    imageEdits: edits.imageEdits,
    imagePublicIdReplacements: edits.imagePublicIdReplacements,
    offerImageOrderOverrides: order.offerImageOrderOverrides,
    savedOfferGalleryOrder: order.savedOfferGalleryOrder,
    savedRoomImageOrders: order.savedRoomImageOrders,
    isUploadingImages: upload.isUploadingImages,
    isSavingChanges: order.isSavingChanges,
    pendingOversizedUpload: upload.pendingOversizedUpload,
    mediaImages: mediaImagesWithTitles,
    roomImagePendingDeletion: visibility.roomImagePendingDeletion,
    roomImageOrderOverrides: order.roomImageOrderOverrides,
    roomImageOrderResetKey: order.roomImageOrderResetKey,
    roomImagePendingEdit: edits.roomImagePendingEdit,
    uploadedRoomAssignments: upload.uploadedRoomAssignments,
    videoId,
    closeRoomImageDeleteDialog: visibility.closeRoomImageDeleteDialog,
    confirmRemoveRoomImage: visibility.confirmRemoveRoomImage,
    deleteImage: visibility.deleteImage,
    discardRoomImageTitleEdits: edits.discardRoomImageTitleEdits,
    removeRoomImageFromEditor: visibility.removeRoomImageFromEditor,
    removeImageFromRoom: visibility.removeImageFromRoom,
    reorderOfferGalleryImages: order.reorderOfferGalleryImages,
    reorderRoomImages: order.reorderRoomImages,
    saveRoomImageChanges: order.saveRoomImageChanges,
    saveImagesVideoChanges: order.saveImagesVideoChanges,
    discardChanges: order.discardChanges,
    saveRoomImageTitleEdits: edits.saveRoomImageTitleEdits,
    saveImageEdit: edits.saveImageEdit,
    setRoomImagePendingEdit: edits.setRoomImagePendingEdit,
    setVideoId: setLocalVideoId,
    toggleHiddenImage: visibility.toggleHiddenImage,
    updateImageTitle: edits.updateImageTitle,
    uploadFiles: upload.uploadFiles,
    uploadFilesToOfferGallery: upload.uploadFilesToOfferGallery,
    uploadFilesToRoom: upload.uploadFilesToRoom,
    cancelOversizedUpload: upload.cancelOversizedUpload,
    confirmOversizedUpload: upload.confirmOversizedUpload,
    editRoomImage: edits.editRoomImage,
    importImages: mediaImport.importImages,
    importRoomImages: mediaImport.importRoomImages,
    syncGalleryImages: mediaImport.syncGalleryImages,
    syncTopRoomImages: mediaImport.syncTopRoomImages,
    syncAllImages: mediaImport.syncAllImages,
  };
}
